/**
 * SpanishContentPage — lightweight reusable template for ES informational
 * pages that don't need the full service-page structure (FAQ, benefits grid,
 * etc.). Used for: About sub-pages, Team, Offices, Testimonials, Financing,
 * Press Releases, Legal pages, Implant Center sub-pages, etc.
 *
 * Each page passes a `content` object:
 *   { metaTitle, metaDescription, esPath, enPath, h1, subtitle, sections: [{title, body}] }
 */
import React from 'react';
import { Link } from 'react-router-dom';
import EsSEO from '@/components/seo/EsSEO';
import BilingualForm from '@/components/BilingualForm';
import MonthlyPaymentMatcherEs from '@/components/forms/MonthlyPaymentMatcherEs';
import useSpanishTrackingNumber from '@/hooks/useSpanishTrackingNumber';

const SITE = 'https://fortworthdental.com';

const SpanishContentPage = ({ content }) => {
  const c = content;
  const { displayNumber, telLink } = useSpanishTrackingNumber();
  return (
    <>
      <EsSEO
        title={c.metaTitle}
        description={c.metaDescription}
        canonical={c.esPath}
        hreflangEn={c.enPath}
        noindex={c.noindex}
        schema={c.schema || {
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: c.h1,
          inLanguage: 'es-US',
          url: `${SITE}${c.esPath}`,
        }}
      />
      <section className="bg-gradient-to-br from-teal-700 via-teal-600 to-emerald-600 text-white py-14">
        <div className="container mx-auto px-4 max-w-5xl">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-3">{c.h1}</h1>
          {c.subtitle && <p className="text-lg text-teal-50 max-w-3xl">{c.subtitle}</p>}
        </div>
      </section>
      {c.showMatcher && (
        <section className="py-12 bg-gray-50">
          <div className="container mx-auto px-4 max-w-2xl">
            <MonthlyPaymentMatcherEs page={c.esPath} />
          </div>
        </section>
      )}
      <section className="py-14 bg-white">
        <div className="container mx-auto px-4 max-w-4xl prose prose-lg">
          {c.sections.map((s, i) => (
            <div key={i} className="mb-8">
              {s.title && <h2 className="text-2xl font-bold text-gray-900 mb-3">{s.title}</h2>}
              {Array.isArray(s.body) ? s.body.map((p, j) => (
                <p key={j} className="text-gray-700 leading-relaxed mb-3">{p}</p>
              )) : <p className="text-gray-700 leading-relaxed mb-3">{s.body}</p>}
              {s.bullets && (
                <ul className="text-gray-700 space-y-1 list-disc ml-6">
                  {s.bullets.map((b, j) => <li key={j}>{b}</li>)}
                </ul>
              )}
            </div>
          ))}
          {c.showForm && (
            <div className="mt-10 bg-gray-50 rounded-2xl p-6 border border-gray-200">
              <h3 className="text-xl font-bold text-gray-900 mb-1">Solicita tu cita</h3>
              <p className="text-sm text-gray-600 mb-4">Te llamamos en menos de 1 día hábil.</p>
              <BilingualForm lang="es" source={`es_${c.esPath.replace(/\//g, '_')}`} />
            </div>
          )}
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/es/contacto" className="bg-teal-600 hover:bg-teal-700 text-white font-semibold px-7 py-3 rounded-md">
              Reservar Cita
            </Link>
            <a href={telLink} className="bg-white border-2 border-teal-600 text-teal-600 font-semibold px-7 py-3 rounded-md hover:bg-teal-50">
              Llamar: {displayNumber}
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default SpanishContentPage;
